import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const NON_PAID = ['issued', 'pending_check', 'insured', 'overdue', 'disputed', 'claimed'];
const RISKY_GRADES = ['D', 'E', 'F'];

interface RiskItem {
  debtorId: string;
  debtorName: string;
  title: string;
  detail: string;
  severity: 'high' | 'medium';
  tag: string;
}

const fmt = (n: number, max = 0) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: max }).format(n || 0);

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, accessToken, isAuthenticated } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [usage, setUsage] = useState<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    const headers = { Authorization: `Bearer ${accessToken}` };
    fetch(`${API_BASE_URL}/api/settlement/my-usage`, { headers })
      .then((r) => (r.ok ? r.json() : null)).then(setUsage).catch(() => setUsage(null));
    Promise.all([
      fetch(`${API_BASE_URL}/api/invoices`, { headers }).then((r) => (r.ok ? r.json() : { invoices: [] })),
      fetch(`${API_BASE_URL}/api/debtors`, { headers }).then((r) => (r.ok ? r.json() : { debtors: [] })),
      fetch(`${API_BASE_URL}/api/claims`, { headers }).then((r) => (r.ok ? r.json() : { claims: [] })),
    ]).then(([inv, deb, clm]) => {
      const invoices = inv.invoices || [];
      const debtors = deb.debtors || [];
      const claims = clm.claims || [];
      const byId: Record<string, any> = {};
      debtors.forEach((d: any) => { byId[d.id] = d; });

      setStats({
        openAmount: invoices.filter((i: any) => NON_PAID.includes(i.status)).reduce((s: number, i: any) => s + Number(i.amount_gross || 0), 0),
        insuredAmount: invoices.filter((i: any) => i.status === 'insured').reduce((s: number, i: any) => s + Number(i.coverage_amount || 0), 0),
        paidAmount: invoices.filter((i: any) => i.status === 'paid').reduce((s: number, i: any) => s + Number(i.amount_gross || 0), 0),
        openClaims: claims.filter((c: any) => !['paid', 'denied', 'withdrawn'].includes(c.status)).length,
        invoiceCount: inv.total ?? invoices.length,
        debtorCount: deb.total ?? debtors.length,
      });

      // Build risk/action items
      const items: RiskItem[] = [];
      invoices.filter((i: any) => i.status === 'overdue').forEach((i: any) => {
        const d = byId[i.debtor_id];
        items.push({
          debtorId: i.debtor_id,
          debtorName: d?.company_name || 'Onbekende debiteur',
          title: `Factuur ${i.invoice_number} vervallen`,
          detail: `${fmt(Number(i.amount_gross))} openstaand · ${d?.company_name || ''}`,
          severity: 'high',
          tag: 'Vervallen',
        });
      });
      debtors.filter((d: any) => RISKY_GRADES.includes(d.grade)).forEach((d: any) => {
        const open = invoices
          .filter((i: any) => i.debtor_id === d.id && NON_PAID.includes(i.status))
          .reduce((s: number, i: any) => s + Number(i.amount_gross || 0), 0);
        if (open > 0) {
          items.push({
            debtorId: d.id,
            debtorName: d.company_name,
            title: `${d.company_name} — verhoogd risico (grade ${d.grade})`,
            detail: `${fmt(open)} openstaand bij beperkt verzekerbare debiteur`,
            severity: d.grade === 'F' || d.grade === 'E' ? 'high' : 'medium',
            tag: `Grade ${d.grade}`,
          });
        }
      });
      items.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1));
      setRisks(items);
      setReady(true);
    }).catch(() => setReady(true));
  }, [isAuthenticated, accessToken, navigate]);

  if (!user) return <div className="flex items-center justify-center h-screen text-slate-500">Laden...</div>;

  const KPIS = stats ? [
    { label: 'Openstaand', value: fmt(stats.openAmount), sub: `${stats.invoiceCount} facturen`, gradient: 'from-orange-500 to-mfb-orange', icon: '⏳' },
    { label: 'Verzekerd', value: fmt(stats.insuredAmount), sub: 'gedekt door polis', gradient: 'from-mfb-blue to-mfb-blue-light', icon: '🛡️' },
    { label: 'Betaald', value: fmt(stats.paidAmount), sub: 'reeds ontvangen', gradient: 'from-emerald-500 to-emerald-600', icon: '✅' },
    { label: 'Open schades', value: String(stats.openClaims), sub: `${stats.debtorCount} debiteuren`, gradient: 'from-amber-500 to-amber-600', icon: '⚠️' },
  ] : [];

  return (
    <Layout>
      {/* Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-mfb-blue via-mfb-blue to-mfb-blue-light rounded-2xl p-6 sm:p-8 mb-8 text-white">
        <div className="absolute -right-8 -top-8 w-48 h-48 bg-mfb-orange/20 rounded-full blur-2xl" />
        <div className="relative">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mb-1">Welkom terug, {user.full_name?.split(' ')[0]}</h1>
          <p className="text-white text-sm max-w-xl">De zekerheid dat uw factuur betaald wordt — als u wilt zelfs binnen 24 uur.</p>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {KPIS.length === 0
          ? [0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-200 animate-pulse" />)
          : KPIS.map((k) => (
            <div key={k.label} className={`relative overflow-hidden rounded-2xl p-5 text-white bg-gradient-to-br ${k.gradient} shadow-sm`}>
              <div className="absolute right-3 top-3 text-2xl opacity-80">{k.icon}</div>
              <p className="text-xs font-medium uppercase tracking-wide text-white/80">{k.label}</p>
              <p className="text-2xl font-bold mt-1">{k.value}</p>
              <p className="text-xs text-white/80 mt-1">{k.sub}</p>
            </div>
          ))}
      </div>

      {/* Pakketverbruik */}
      {usage && (
        <>
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Pakketverbruik</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <UsageBar label="Opinies (kredietchecks)" used={usage.opinies_gebruikt} max={usage.inbegrepen_opinies} suffix="" />
            <UsageBar label="Verzekerde incasso's" used={usage.incassos_gebruikt} max={usage.inbegrepen_incassos} suffix="" />
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Verzekerd bedrag</p>
              <p className="text-2xl font-bold mt-1 text-mfb-blue">{fmt(usage.verzekerd_bedrag)}</p>
              <p className="text-xs text-slate-500 mt-1">gedekt door polis</p>
            </div>
          </div>
        </>
      )}

      {/* Aandacht nodig */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-semibold text-mfb-blue">Aandacht nodig</h2>
        <button onClick={() => navigate('/debtors')} className="text-sm font-semibold text-mfb-orange hover:underline">
          Alle debiteuren →
        </button>
      </div>

      {!ready ? (
        <div className="space-y-3 mb-10">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-slate-200 animate-pulse" />)}</div>
      ) : risks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500 mb-10">
          🎉 Geen openstaande risico's — alle debiteuren zijn in orde.
        </div>
      ) : (
        <div className="space-y-3 mb-10">
          {risks.map((r, idx) => (
            <button
              key={idx}
              onClick={() => navigate(`/debtors/${r.debtorId}`)}
              className="w-full flex items-center gap-4 bg-white rounded-xl border border-slate-200 hover:border-mfb-orange hover:shadow-sm transition p-4 text-left"
            >
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${r.severity === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{r.title}</p>
                <p className="text-xs text-slate-500 truncate">{r.detail}</p>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${r.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                {r.tag}
              </span>
              <span className="text-mfb-orange font-semibold">→</span>
            </button>
          ))}
        </div>
      )}

      {/* Quick links */}
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Snel naar</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <button onClick={() => navigate('/debtors')} className="text-left bg-white rounded-2xl border border-slate-200 hover:border-mfb-orange hover:shadow-lg transition p-6">
          <div className="w-12 h-12 rounded-xl bg-mfb-blue flex items-center justify-center text-2xl mb-3">👥</div>
          <h3 className="text-lg font-semibold text-mfb-blue mb-1">Debiteuren</h3>
          <p className="text-sm text-slate-600">Bekijk per debiteur de stamdata, het saldo en wat verzekerd is.</p>
        </button>
        <button onClick={() => navigate('/invoices')} className="text-left bg-white rounded-2xl border border-slate-200 hover:border-mfb-orange hover:shadow-lg transition p-6">
          <div className="w-12 h-12 rounded-xl bg-mfb-orange flex items-center justify-center text-2xl mb-3">📄</div>
          <h3 className="text-lg font-semibold text-mfb-blue mb-1">Facturen</h3>
          <p className="text-sm text-slate-600">Alle facturen met status en dekking in één overzicht.</p>
        </button>
      </div>
    </Layout>
  );
};

const UsageBar: React.FC<{ label: string; used: number; max: number; suffix?: string }> = ({ label, used, max }) => {
  const over = max > 0 && used > max;
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const barColor = over ? 'bg-mfb-orange' : pct >= 80 ? 'bg-amber-500' : 'bg-mfb-blue';
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        {over && <span className="text-xs font-semibold text-mfb-orange">bijbetaling</span>}
      </div>
      <p className="text-2xl font-bold mt-1 text-mfb-blue">{used}<span className="text-base font-medium text-slate-400"> / {max}</span></p>
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${over ? 100 : pct}%` }} />
      </div>
      {over && <p className="text-xs text-mfb-orange mt-2">{used - max} boven het pakket → extra servicefee</p>}
    </div>
  );
};

export default DashboardPage;
